<template>
  <v-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
  >
    <v-card>
      <v-container
        :class="{
          'px-10': !mobile,
          'pt-8': !mobile,
          'px-6': mobile,
          'pt-4': mobile
        }"
      >
        <p class="subtitle-1 font-weight-bold mb-2">Waive Rental Fee</p>

        <v-row>
          <v-col class="py-0" md="3">
            <p class="caption">Tenant:</p>
          </v-col>
          <v-col class="py-0" md="9">
            <p class="caption font-weight-bold">{{ tenantName }}</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" md="3">
            <p class="caption">Rent Fee:</p>
          </v-col>
          <v-col class="py-0" md="9">
            <p class="caption font-weight-bold">
              {{ formatCurrency(rentFee) }}
            </p>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <v-form v-model="isFormValid" lazy-validation>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <v-select
                v-model="category"
                :items="categoryItems"
                label="Select Fees to Waive"
                prepend-inner-icon="mdi-tools"
                required
                dense
                outlined
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="category === 'rent'">
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                disabled
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDate"
                    label="Start Date"
                    prepend-inner-icon="mdi-calendar-month"
                    disabled
                    readonly
                    dense
                    outlined
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  no-title
                  scrollable
                  @input="startDateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDate"
                    label="End Date"
                    prepend-inner-icon="mdi-calendar-month"
                    :rules="endDateRules"
                    readonly
                    dense
                    outlined
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  no-title
                  scrollable
                  @input="endDateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              :class="{ 'pb-2': !mobile, 'py-0': mobile }"
              cols="12"
              sm="12"
              md="12"
            >
              <v-checkbox v-model="confirmSendEmail" class="my-0" hide-details>
                <template v-slot:label> Send email to tenant </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="font-weight-bold"
            color="primary darken-1"
            text
            @click="finish"
            >Confirm</v-btn
          >
          <v-btn
            class="font-weight-bold"
            color="grey darken-1"
            text
            @click="cancel"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-container>
    </v-card>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import { dayjs } from '../../util/date'

export default {
  data() {
    return {
      loading: true,
      tenantId: -1,
      rentFee: 0,
      bedId: '',
      dialog: false,
      category: 'rent',
      confirmSendEmail: false,
      isFormValid: false,
      options: {
        color: 'primary',
        width: 500,
        zIndex: 200
      },
      categoryItems: [{ text: 'Rent', value: 'rent' }],
      attachments: [],
      tenantName: '',
      startDate: dayjs()
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .format('YYYY-MM-DD'),
      endDate: dayjs()
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .format('YYYY-MM-DD'),
      // startDatePicker: dayjs().hour(0).minute(0).second(0).millisecond(0).format('YYYY-MM-DD'),
      // endDatePicker: dayjs().hour(0).minute(0).second(0).millisecond(0).format('YYYY-MM-DD'),
      startDateMenu: false,
      endDateMenu: false,
      paidUntilDate: dayjs().hour(0).minute(0).second(0).millisecond(0),
      endDateRules: [
        (v) =>
          dayjs(v).isAfter(dayjs(this.startDate), 'day') ||
          'End Date must be later than start date'
      ],
      leaseId: -1
    }
  },
  computed: {
    mobile() {
      return this.$vuetify.breakpoint.xs
    }
  },
  methods: {
    async open(tenant) {
      this.leaseId = tenant.leaseId
      this.loading = true
      this.tenantId = tenant.tenantId
      this.bedId = tenant.bedId
      this.dialog = true
      this.rentFee = parseFloat(tenant.rentFee)
      this.total = parseFloat(tenant.rentFee)
      this.startDate = dayjs(tenant.nextRentalPeriod.startDate).format(
        'YYYY-MM-DD'
      )
      this.endDate = dayjs(tenant.nextRentalPeriod.endDate).format('YYYY-MM-DD')
      this.paidUntilDate = dayjs(tenant.paidUntilDate)

      if (tenant.tenantAlias) {
        this.tenantName = tenant.tenantAlias
      } else {
        const tenant_details =
          tenant.tenant_details ||
          (tenant.lease_details && tenant.lease_details.tenant_details)
        if (tenant_details.firstName)
          this.tenantName += tenant_details.firstName
        if (tenant_details.lastName)
          this.tenantName += ' ' + tenant_details.lastName
        if (
          !tenant_details.firstName &&
          !tenant_details.lastName &&
          tenant_details.username
        )
          this.tenantName = tenant_details.username
      }
      this.attachments = []
      //this.options = Object.assign(this.options, options)

      this.loading = false
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    async finish() {
      try {
        this.loading = true
        await this.$store.dispatch('payments/createRentalPaymentAction', {
          tenantId: this.tenantId,
          bedId: this.bedId,
          rentFee: this.rentFee,
          startDate: dayjs(this.startDate).format(),
          endDate: dayjs(this.endDate).format(),
          attachments: this.attachments,
          waived: true,
          amount: 0,
          leaseId: this.leaseId,
          confirmSendEmail: this.confirmSendEmail
        })
        this.dialog = false
        this.loading = false
        this.$router.go()
      } catch (err) {
        this.$root
          .$confirm({
            title: 'Waive Rental Fee',
            message: err,
            reconfirmOnly: true,
            confirmText: 'OK',
            options: {
              color: 'error'
            }
          })
          .then(() => {
            this.loading = false
          })
      }
    },
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    },
    cancel() {
      this.dialog = false
    }
  }
}
</script>

<style scoped>
p {
  margin-bottom: 0 !important;
}
</style>
